<template>
  <v-data-table
    v-model="selected"
    class="archive-table"
    style="width: 100%"
    :headers="headers"
    :items="items"
    :loading="loading"
    :page.sync="pageLocal"
    :items-per-page.sync="sizeLocal"
    :server-items-length="totalItems"
    item-key="id"
    show-select
    hide-default-footer
  >
    <!-- Top toolbar -->
    <template #top>
      <v-row class="pa-4 mb-2" align="center" justify="space-between">
        <v-text-field
          v-model="searchQuery"
          append-icon="mdi-magnify"
          :placeholder="$t('Izlash...')"
          dense
          clearable
          outlined
          hide-details
          class="search-input mr-4"
          style="max-width: 320px"
          @input="onSearch"
        />

        <div class="d-flex align-center">
          <v-btn
            class="btn-primary-soft mr-2"
            :class="{ 'btn-disabled': !selected.length }"
            :disabled="!selected.length"
            depressed
            ripple="false"
            @click="restoreSelected"
          >
            <v-icon left>mdi-backup-restore</v-icon>
            {{$t('Restore')}}
          </v-btn>

          <!-- NEW: Delete button -->
          <v-btn
            class="btn-danger-soft"
            :class="{ 'btn-disabled': !selected.length }"
            :disabled="!selected.length"
            depressed
            ripple="false"
            @click="deleteSelected"
          >
            <v-icon left>mdi-delete-forever</v-icon>
            {{$t('Delete')}}
          </v-btn>
        </div>
      </v-row>

      <!-- Restore dialog -->
      <v-dialog v-model="dialogRestore" max-width="520" :retain-focus="false">
        <v-card class="rounded-lg">
          <v-card-title class="headline font-weight-bold">
            {{$t('Restore selected records?')}}
          </v-card-title>

          <v-card-text>
            <div class="mb-4">
              {{$t('You are about to restore')}}
              <strong>{{ selected.length }}</strong>
              {{$t('item(s) from the archive')}}.
            </div>

            <v-sheet outlined rounded class="pa-3 mb-4">
              <div class="subtitle-2 mb-2">{{$t('Preview')}}</div>
              <div v-if="selected.length">
                <div
                  v-for="s in selected.slice(0, 5)"
                  :key="s.id"
                  class="d-flex align-center mb-1"
                >
                  <v-icon small class="mr-2" color="primary">mdi-file-restore</v-icon>
                  <span class="caption">#{{ s.contractNumber }} — {{ s.firstName }} {{ s.lastName }}</span>
                </div>
                <div v-if="selected.length > 5" class="caption grey--text">
                  + {{ selected.length - 5 }} {{$t('more')}}
                </div>
              </div>
              <div v-else class="caption grey--text">
                {{$t('No items selected')}}
              </div>
            </v-sheet>

            <v-alert
              v-if="restoreErrors.length"
              type="error"
              dense
              class="mb-2"
            >
              {{$t('Some items failed to restore')}}: {{ restoreErrors.length }}
            </v-alert>

            <v-progress-linear
              v-if="restoring"
              indeterminate
              rounded
              class="mb-2"
            />
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn
              class="btn-ghost mr-2"
              text
              ripple="false"
              @click="dialogRestore = false"
              :disabled="restoring"
            >
              {{$t('Cancel')}}
            </v-btn>
            <v-btn
              class="btn-primary-soft"
              :loading="restoring"
              depressed
              ripple="false"
              @click="confirmRestore"
            >
              {{$t('Confirm')}}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- NEW: Permanent delete dialog -->
      <v-dialog v-model="dialogDelete" max-width="520" :retain-focus="false">
        <v-card class="rounded-lg">
          <v-card-title class="headline font-weight-bold red--text text--darken-1">
            {{$t('Permanently delete selected?')}}
          </v-card-title>

          <v-card-text>
            <div class="mb-4">
              <strong class="red--text text--darken-1">{{$t('Warning')}}:</strong>
              {{$t('This action cannot be undone. You are about to permanently delete')}}
              <strong>{{ selected.length }}</strong>
              {{$t('item(s).')}}
            </div>

            <v-sheet outlined rounded class="pa-3 mb-4">
              <div class="subtitle-2 mb-2">{{$t('Preview')}}</div>
              <div v-if="selected.length">
                <div
                  v-for="s in selected.slice(0, 5)"
                  :key="s.id"
                  class="d-flex align-center mb-1"
                >
                  <v-icon small class="mr-2" color="red darken-1">mdi-delete</v-icon>
                  <span class="caption">#{{ s.contractNumber }} — {{ s.firstName }} {{ s.lastName }}</span>
                </div>
                <div v-if="selected.length > 5" class="caption grey--text">
                  + {{ selected.length - 5 }} {{$t('more')}}
                </div>
              </div>
              <div v-else class="caption grey--text">
                {{$t('No items selected')}}
              </div>
            </v-sheet>

            <v-alert
              v-if="deleteErrors.length"
              type="error"
              dense
              class="mb-2"
            >
              {{$t('Some items failed to delete')}}: {{ deleteErrors.length }}
            </v-alert>

            <v-progress-linear
              v-if="deleting"
              indeterminate
              rounded
              class="mb-2"
              color="red darken-1"
            />
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn
              class="btn-ghost mr-2"
              text
              ripple="false"
              @click="dialogDelete = false"
              :disabled="deleting"
            >
              {{$t('Cancel')}}
            </v-btn>
            <v-btn
              class="btn-danger-soft"
              :loading="deleting"
              depressed
              ripple="false"
              @click="confirmDelete"
            >
              <v-icon left>mdi-delete-forever</v-icon>
              {{$t('Delete permanently')}}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Toast -->
      <v-snackbar v-model="snack.visible" :timeout="3000" top right>
        {{ snack.text }}
      </v-snackbar>
    </template>

    <!-- Full name -->
    <template #item.fullName="{ item }">
      {{ item.firstName }} {{ item.lastName }}
    </template>

    <!-- Deleted date -->
    <template #item.deleted_date="{ item }">
      {{ formattedDeletedDate(item.deleted_date) }}
    </template>

    <!-- Footer pagination -->
    <template #footer>
      <v-row class="pa-4" align="center" justify="center">
        <v-select
          v-model="sizeLocal"
          :items="[10, 25, 50, 100]"
          dense
          outlined
          hide-details
          style="max-width: 100px"
        />
        <v-pagination
          v-model="pageLocal"
          :length="pageCount"
          total-visible="7"
          prev-icon="mdi-chevron-left"
          next-icon="mdi-chevron-right"
          class="mx-4"
        />
      </v-row>
    </template>
  </v-data-table>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ArchiveTable',
  data() {
    return {
      selected: [],
      items: [],
      loading: false,
      totalItems: 0,
      searchQuery: '',
      pageLocal: 1,
      sizeLocal: 100,

      // Restore flow
      dialogRestore: false,
      restoring: false,
      restoreErrors: [],

      // Delete flow
      dialogDelete: false,
      deleting: false,
      deleteErrors: [],

      snack: { visible: false, text: '' },

      headers: [
        { text: '',            value: 'data-table-select' },
        { text: this.$t ? this.$t('Contract #') : 'Contract #',  value: 'contractNumber' },
        { text: this.$t ? this.$t('Date') : 'Date',              value: 'deleted_date' },
        { text: 'F.I.O',       value: 'fullName', sortable: false },
        { text: this.$t ? this.$t('Status') : 'Status',          value: 'paidStatus' }
      ]
    };
  },
  computed: {
    pageCount() {
      return Math.ceil(this.totalItems / this.sizeLocal) || 1;
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const res = await axios.get('admin/contract2025/get/deleted', {
          params: {
            page: this.pageLocal - 1,
            size: this.sizeLocal,
            locale: this.$i18n ? this.$i18n.locale : 'uz',
            searchQuery: this.searchQuery || ''
          }
        });
        this.items = res.data.content || [];
        this.totalItems = res.data.totalElements || 0;
      } finally {
        this.loading = false;
      }
    },

    onSearch() {
      this.pageLocal = 1;
      this.fetchData();
    },

    formattedDeletedDate(deleted_date) {
      if (!deleted_date) return '--';
      const d = new Date(deleted_date);
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${d.getDate()} ${monthNames[d.getMonth()]}, ${hh}:${mm}`;
    },

    // --- Restore ---
    restoreSelected() {
      if (!this.selected.length) return;
      this.restoreErrors = [];
      this.dialogRestore = true;
    },

    async confirmRestore() {
      if (!this.selected.length) return;
      this.restoring = true;
      this.restoreErrors = [];
      try {
        const results = await Promise.allSettled(
          this.selected.map(item => axios.put(`admin/contract2025/restore/${item.id}`))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length) {
          this.restoreErrors = failed.map(f => f.reason);
          this.snack = { visible: true, text: this.$t ? this.$t('Restored with some errors') : 'Restored with some errors' };
        } else {
          this.snack = { visible: true, text: this.$t ? this.$t('Restored successfully') : 'Restored successfully' };
        }
      } catch (e) {
        this.restoreErrors = [e];
        this.snack = { visible: true, text: this.$t ? this.$t('Restore failed') : 'Restore failed' };
      } finally {
        this.restoring = false;
        this.dialogRestore = false;
        this.selected = [];
        this.fetchData();
      }
    },

    // --- Delete (permanent) ---
    deleteSelected() {
      if (!this.selected.length) return;
      this.deleteErrors = [];
      this.dialogDelete = true;
    },

    async confirmDelete() {
      if (!this.selected.length) return;
      this.deleting = true;
      this.deleteErrors = [];
      try {
        if (this.selectAll) {
          await axios.delete(`/admin/contract2025/archive/hard/delete/all`, {
            params: {
              searchQuery: this.searchQuery || '',
              locale: this.$i18n ? this.$i18n.locale : 'uz'
            }
          });
        } else {
          const results = await Promise.allSettled(
              this.selected.map(item =>
                  axios.delete(`/admin/contract2025/archive/hard/delete/${item.id}`)
              )
          );
          const failed = results.filter(r => r.status === 'rejected');
          if (failed.length) {
            this.deleteErrors = failed.map(f => f.reason);
            this.snack = { visible: true, text: this.$t('Deleted with some errors') };
          } else {
            this.snack = { visible: true, text: this.$t('Deleted successfully') };
          }
        }
      } catch (e) {
        this.deleteErrors = [e];
        this.snack = { visible: true, text: this.$t('Delete failed') };
      } finally {
        this.deleting = false;
        this.dialogDelete = false;
        this.selected = [];
        this.fetchData();
      }
    }
  },
  watch: {
    pageLocal() { this.fetchData(); },
    sizeLocal() { this.pageLocal = 1; this.fetchData(); }
  },
  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.archive-table { width: 100%; }

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}

/* --- Buttons (modern, light) --- */
.v-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: .2px;
  transition: box-shadow .18s ease, transform .12s ease, background-color .18s ease, border-color .18s ease;
}

.btn-primary-soft {
  background: #f3f6ff;
  border: 1px solid #e0e7ff;
  color: #1f4bdd;
}
.btn-primary-soft:hover {
  box-shadow: 0 4px 14px rgba(31, 75, 221, .15);
  transform: translateY(-1px);
  background: #eef2ff;
}
.btn-primary-soft:active { transform: translateY(0); }

/* NEW: Danger soft */
.btn-danger-soft {
  background: #fff5f5;
  border: 1px solid #ffdada;
  color: #b71c1c;
}
.btn-danger-soft:hover {
  box-shadow: 0 4px 14px rgba(183, 28, 28, .15);
  transform: translateY(-1px);
  background: #ffecec;
}
.btn-danger-soft:active { transform: translateY(0); }

/* Disabled */
.btn-disabled {
  background: #f6f7f9 !important;
  border: 1px solid #eceff3 !important;
  color: #9aa3af !important;
  box-shadow: none !important;
}

/* Ghost (Cancel) */
.btn-ghost {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  color: #374151 !important;
}
.btn-ghost:hover {
  box-shadow: 0 4px 12px rgba(17, 24, 39, .08);
  transform: translateY(-1px);
}

/* Cards & sheets */
.v-card { border-radius: 12px; }
.v-sheet { border-radius: 8px; }
</style>
